<template>
  <!-- SOURCE: https://github.com/albertopereira/vuejs-treemap -->
  <div
    :style="{height: innerHeight + 'px', width: innerWidth + 'px'}"
    class="treemap mt-4"
  >
    <h2
      v-if="showTitle"
      class="text-black d-inline-block mb-3"
    >
      {{ capitalise(type) }}s Engagement
    </h2>

    <popover
      v-if="showTitle"
    >
      Engagement is calculated as the sum of engaged minutes by each user within the account for the selected period. Depending on the timeframe you are viewing, and the time of day you are viewing it, some of the data displayed here is delayed.
    </popover>

    <div
      v-if="emptyData"
      class="treemap__empty chart-grey-background text-center mb-3"
      :style="{ height: innerHeight - 48 + 'px', width: innerWidth + 'px'}"
    >
      <div class="inner">
        No data
      </div>
    </div>

    <div
      class="treemap__wrapper"
    >
      <!-- The SVG structure is explicitly defined in the template with attributes derived from component data -->
      <svg
        v-if="isReady"
        :width="svgWidth"
        :height="svgHeight"
        class="treemap-svg"
        style="margin-left: 0px;"
      >
        <!-- Vue transitions -->
        <transition-group
          name="list"
          tag="g"
          class="depth"
        >
          <!-- Generate each of the visible squares at a given zoom level (the current selected node) -->
          <g
            v-for="(children, index) in selectedNode._children"
            :key="'c_' + children.id + '_' + index"
            class="children"
          >
            <!--
                The visible square rect element.
                You can attribute directly an event, that fires a method that changes the current node, restructuring the data tree, that reactivly gets reflected in the template.
              -->
            <router-link
              v-if="children.itemId && enableLink"
              :to="`/${type === 'company' ? 'companie' : type === 'partner' ? 'channel' : type}s/${children.itemId}`"
            >
              <rect
                :id="children.id"
                :key="children.id"
                :itemId="children.itemId"
                :x="x(children.x0)"
                :y="y(children.y0)"
                :width="rectWidth(children)"
                :height="rectHeight(children)"
                :style="{ fill: getColorFill(children.data) }"
                class="parent"
                @click="selectNode"
              >
                <!-- The title attribute -->
                <title>{{ children.data.name }}  | {{ children.data.value }}</title>
              </rect>
            </router-link>

            <rect
              v-else-if="children.itemId"
              :id="children.id"
              :key="children.id"
              :itemId="children.itemId"
              :x="x(children.x0)"
              :y="y(children.y0)"
              :width="rectWidth(children)"
              :height="rectHeight(children)"
              :style="{ fill: getColorFill(children.data) }"
              class="parent"
              @click="selectNode"
            >
              <!-- The title attribute -->
              <title>{{ children.data.name }}  | {{ children.data.value }}</title>
            </rect>

            <g
              v-if="minSize(children)"
              class="heart-wrapper"
              :transform="`translate(${x(children.x0) + 10}, ${y(children.y0) + 4})`"
            >
              <impact-icon
                :color="getColorFont(children.data)"
              />
            </g>

            <text
              v-if="minSize(children)"
              :key="'t_' + children.id + '_value'"
              :x="x(children.x0) + 28"
              :y="y(children.y0) + 5"
              dy="1em"
              :style="{ fill: getColorFont(children.data) }"
              class="value"
            >
              {{ children.value }}
            </text>

            <!-- The visible square text element with the title and value of the child node -->
            <WrapText
              v-if="minSize(children)"
              :key="'t_' + children.id + '_name'"
              :inputText="children.data.name"
              :modePercentage="modePercentage"
              :percentage="children.data.diffStr"
              :x="x(children.x0) + 10"
              :y="y(children.y0) + 6"
              :width="x(children.x1 - children.x0 + children.parent.x0)"
              :dy="3"
              :inputStyle="{ fill: getColorFont(children.data) }"
              class="subtitle"
            />
          </g>
        </transition-group>
      </svg>

      <div
        v-if="!isReady"
        class="loading-wrapper chart-grey-background"
        :style="{height: svgHeight + 'px', width: svgWidth + 'px'}"
      >
        <loading-icon
          :loading="!isReady"
          loading-class="text-center vertical-middle"
        />
      </div>

      <div
        v-if="showLegend && !emptyData && isReady"
        class="legend-wrapper pb-2"
      >
        <color-range-legend
          :items="legendItems"
        />
      </div>
    </div>
  </div>
</template>

<script>
import textures from 'textures'
import d3 from '@/analytics/importers/d3-importer'
import {capitalise, average} from '@/analytics/utils/data-analyser-utils'

import ImpactIcon from './ImpactIcon'
import ColorRangeLegend from './ColorRangeLegend'
import WrapText from './WrapText'

const props = {
  items: {
    type: Array,
    required: false,
  },
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  minWidth: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    default: 'accounts',
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  mode: {
    type: String,
    default: 'percentage',
  },
  enableLink: {
    type: Boolean,
    default: true,
  },
}

export default {
  name: 'Treemap',
  components: {
    ImpactIcon,
    ColorRangeLegend,
    WrapText,
  },
  props: props,
  data () {
    return {
      rootNode: {},
      margin: {
        top: 20,
        right: 0,
        bottom: 0,
        left: 0,
      },
      selected: null,
      colors: null,

    }
  },
  computed: {
    legendItems () {
      const colors = this.colors.pos.slice().reverse().concat([this.colors.neu]).concat(this.colors.neg)

      return {
        colors: colors,
        labels: {
          left: 'Positive',
          right: 'Negative',
        },
      }
    },
    treemapItems () {
      return {name: `${capitalise(this.type)} Engagement`, children: this.items}
    },
    innerWidth () {
      return this.width
    },
    innerHeight () {
      return this.emptyData ? 200 : this.height
    },
    svgWidth () {
      return Math.max(this.minWidth, this.innerWidth - this.margin.left - this.margin.right)
    },
    svgHeight () {
      return this.innerHeight - this.margin.top - this.margin.bottom - (this.modePercentage ? 145 : 0)
    },
    showLegend () {
      return this.colors && !this.emptyData && this.modePercentage
    },
    // The grandparent id
    parentId () {
      if (this.selectedNode.parent === undefined || this.selectedNode.parent === null) {
        return this.selectedNode.id
      } else {
        return this.selectedNode.parent.id
      }
    },
    // Returns the x position within the current domain
    // Maybe it can be replaced by a vuejs method
    x () {
      return d3.scaleLinear()
          .domain([0, this.svgWidth])
          .range([0, this.svgWidth])
    },
    // Returns the y position within the current domain
    // Maybe it can be replaced by a vuejs method
    y () {
      return d3.scaleLinear()
          .domain([0, this.svgHeight])
          .range([0, this.svgHeight])
    },
    // The D3 function that recursively subdivides an area into rectangles
    treemap () {
      return d3.treemap()
          .size([this.svgWidth, this.svgHeight])
          .round(false)
          .paddingInner(0)
    },
    // The current selected node
    selectedNode () {
      let node = null

      if (this.selected) {
        const nd = this.getNodeById(this.rootNode, this.selected, this)

        if (nd._children) {
          node = nd
        } else {
          node = nd.parent
        }
      } else {
        node = this.rootNode
      }

      // Recalculates the y and x domains
      this.x.domain([node.x0, node.x0 + (node.x1 - node.x0)])
      this.y.domain([node.y0, node.y0 + (node.y1 - node.y0)])

      return node
    },
    isReady () {
      return this.items != null && this.selectedNode != null
    },
    emptyData () {
      return this.items != null && !this.items.length
    },
    modePercentage () {
      return this.mode === 'percentage'
    },
    modeSurface () {
      return this.mode === 'surface'
    },
    itemValues () {
      return this.items.map((it) => it.value)
    },
    valueMax () {
      return Math.max(...this.itemValues)
    },
    valueMin () {
      return Math.min(...this.itemValues)
    },
    colorScale () {
      return this.valueMax > average(this.itemValues) * 5 ? 'log' : 'linear'
    },
  },
  // You can do whatever when the selected node changes
  watch: {
    selectedNode () {
      // console.log('The selected node changed...')
    },
    items (items) {
      if (items) {
        this.initializeTreemap()
      }
    },
    width () {
      this.initializeTreemap()
    },
  },
  // In the beginning...
  mounted () {
    this.initializeTreemap()
  },
  methods: {
    initializeTreemap () {
      this.$nextTick(() => {
        if (!this.items) {
          return
        }

        // SVG DOM element
        this.svg = this.$el.getElementsByClassName('treemap-svg')[0]
        this.setupColors()

        // loads the data and calls the initialization methods
        this.initialize()
        this.accumulate(this.rootNode, this)
        this.treemap(this.rootNode)
        this.$forceUpdate()
      })
    },
    getColorFill (data) {
      if (this.modePercentage) {
        return this.getPercentageColor(data.diffNum)
      } else if (this.modeSurface) {
        return this.getSurfaceColor(data).fill
      } else {
        return '#e8e5d7'
      }
    },
    getColorFont (data) {
      if (this.modePercentage) {
        return '#1f2638'
      } else if (this.modeSurface) {
        return this.getSurfaceColor(data).font
      } else {
        return '#1f2638'
      }
    },
    getPercentageColor (perc) {
      const colorsLen = this.colors.pos.length - 1

      if (!perc || perc === 0) {
        return this.colors.neu
      } else if (perc > 0) {
        return this.colors.pos[Math.min(Math.round(perc / 100 * colorsLen), colorsLen)]
      } else {
        return this.colors.neg[Math.min(Math.round(Math.abs(perc) / 100 * colorsLen), colorsLen)]
      }
    },
    getSurfaceColor (data) {
      const colorsLen = this.colors.length - 1
      let index = 0


      if (this.colorScale === 'log') {
        index = data.value > this.valueMin ? Math.min(Math.round(Math.log(data.value - this.valueMin) / Math.log(this.valueMax) * colorsLen), colorsLen) : 0
      } else if (this.colorScale === 'linear') {
        index = data.value > this.valueMin ? Math.min(Math.round((data.value - this.valueMin) / this.valueMax * colorsLen), colorsLen) : 0
      }

      // console.log(data, index, this.colors)
      return this.colors[index]
    },
    setupColors () {
      if (this.modePercentage) {
        const posColors = ['#cfd6e0', '#b3c6e8', '#94b6f0', '#6ea8f8', '#2d9aff']
        const negColors = ['#f3d1ab', '#f8a281', '#fc7866', '#fe5c56', '#ff4343']
        const negFills = []

        for (const col of negColors) {
          const t = textures
              .lines()
              .stroke(col)
              .strokeWidth(5)
              .size(8)

          d3.select(this.svg).call(t)
          negFills.push(t.url())
        }

        this.colors = {
          neu: '#e8e5d7',
          pos: posColors,
          neg: negFills,
        }
      } else if (this.modeSurface) {
        this.colors = [
          {fill: '#c4f5ec', font: '#1f2638'},
          {fill: '#81dee6', font: '#1f2638'},
          {fill: '#34c5e1', font: '#1f2638'},
          {fill: '#38a4df', font: 'white'},
          {fill: '#3082dd', font: 'white'},
          {fill: '#1561da', font: 'white'},
          {fill: '#014ab2', font: 'white'},
          {fill: '#013680', font: 'white'},
          {fill: '#002451', font: 'white'},
        ]
      }
    },
    // Called once, to create the hierarchical data representation
    initialize () {
      if (this.items) {
        this.rootNode = d3.hierarchy(this.treemapItems)
            .eachBefore(function (d) {
              d.id = (d.parent ? d.parent.id + '.' : '') + (d.data.id ? '.' + d.data.id : ''); d.itemId = d.data.id ? d.data.id : ''
            })
            .sum((d) => d.value)
            .sort(function (a, b) {
              return b.height - a.height || b.value - a.value
            })

        this.rootNode.x = 0
        this.rootNode.y = 0
        this.rootNode.x1 = this.svgWidth
        this.rootNode.y1 = this.svgHeight
        this.rootNode.depth = 0
      }
    },
    // Calculates the accumulated value (sum of children values) of a node - its weight,
    // represented afterwards in the area of its square
    accumulate (d, context) {
      d._children = d.children

      if (d._children) {
        d.value = d._children.reduce(function (p, v) {
          return p + context.accumulate(v, context)
        }, 0)
        return d.value
      } else {
        return d.value
      }
    },
    // Helper method - gets a node by its id attribute
    getNodeById (node, id, context) {
      if (node.id === id) {
        return node
      } else if (node._children) {
        for (let i = 0; i < node._children.length; i++) {
          const nd = context.getNodeById(node._children[i], id, context)
          if (nd) {
            return nd
          }
        }
      }
    },
    // When a user clicks a square, changes the selected data attribute
    // which fires the computed selectedNode, which in turn finds the Node by the id of the square clicked
    // and the template reflects the changes
    selectNode (event) {
      // this.selected = event.target.id
    },
    capitalise (str) {
      return capitalise(str)
    },
    rectHeight (data) {
      return this.y(data.y1 - data.y0 + data.parent.y0)
    },
    rectWidth (data) {
      return this.x(data.x1 - data.x0 + data.parent.x0)
    },
    minSize (data) {
      return data && data.parent && this.rectHeight(data) > 55 && this.rectWidth(data) > 65
    },
  },
}
</script>

<style lang="scss">
  .treemap {
    position: relative;
    overflow: hidden;
    &__empty {
      position: relative;
      .inner {
        position: relative;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    &__wrapper {
      overflow: hidden;
      overflow-x: auto;
    }

    text {
      pointer-events: none;
    }
    rect {
      fill: none;
      stroke: #fff;
      &.parent {
        stroke-width: 2px;
      }
    }
    .children {
      overflow: hidden;
      rect.parent {
        fill: #bbb;
        fill-opacity: 1;
      }
      text {
        fill: #1f2638;
        font-family: $font-family-sans-serif;
        &.value {
          font-size: 18px;
        }
        &.subtitle {
          font-size: 11px;
        }
      }
    }
    .legend-wrapper {
      margin-top: 24px;
    }
  }
</style>
